/**
 * Permission utility functions
 */

/**
 * Permission scope configuration matching backend
 */
export const PERMISSION_SCOPE_CONFIG: Record<string, { fieldName?: string; hasScope: boolean }> = {
  // Product permissions - No scope filtering
  'product:view': { hasScope: false },
  'product:view_cost': { hasScope: false },
  'product:view_suppliers': { hasScope: false },
  'product:create_edit': { hasScope: false },
  'product:edit_cost': { hasScope: false },
  'product:edit_price': { hasScope: false },
  'product:export': { hasScope: false },
  'product:delete': { hasScope: false },
  'discount:view': { hasScope: false },
  'discount:create_edit': { hasScope: false },
  'discount:delete': { hasScope: false },

  // Procurement analysis permissions - Buyer-based filtering
  'procurement_analysis:dashboard_view': { fieldName: 'buyer_id', hasScope: true },
  'procurement_analysis:module_view': { fieldName: 'buyer_id', hasScope: true },
  'procurement_analysis:data_errors_view': { fieldName: 'buyer_id', hasScope: true },
  'procurement_analysis:history_performance_view': { fieldName: 'buyer_id', hasScope: true },

  // Purchase permissions - Buyer-based filtering
  'purchase:opportunity_view': { fieldName: 'assignedTo', hasScope: true },
  'purchase:opportunity_create_edit': { fieldName: 'assignedTo', hasScope: true },
  'purchase:opportunity_assign': { fieldName: 'assignedTo', hasScope: true },
  'purchase:opportunity_delete': { fieldName: 'assignedTo', hasScope: true },
  'purchase:opportunity_review_cannot_contact': {
    fieldName: 'assignedTo',
    hasScope: true,
  },
  'purchase:procurement_goal_view': { fieldName: 'ownerUserId', hasScope: true },
  'purchase:procurement_goal_create_edit': { fieldName: 'ownerUserId', hasScope: true },
  'purchase:procurement_goal_follow': { hasScope: false },
  'purchase:procurement_goal_complete': { hasScope: false },
  'purchase:quote_view': { fieldName: 'buyer_id', hasScope: true },
  'purchase:quote_create_edit': { fieldName: 'buyer_id', hasScope: true },
  'purchase:quote_delete': { fieldName: 'buyer_id', hasScope: true },
  'purchase:order_view': { fieldName: 'buyer_id', hasScope: true },
  'purchase:order_create_edit': { fieldName: 'buyer_id', hasScope: true },
  'purchase:order_delete': { fieldName: 'buyer_id', hasScope: true },

  // Supplier permissions
  'supplier:view': { fieldName: 'buyer_relationship', hasScope: true },
  'supplier:create_edit': { fieldName: 'created_by', hasScope: true },
  'supplier:delete': { fieldName: 'created_by', hasScope: true },
  'supplier_platform_group:view': { hasScope: false },
  'supplier_platform_group:match': { hasScope: false },
  'supplier_platform_message:view': { hasScope: false },
  'supplier_platform_message:follow_up': { hasScope: false },
  'supplier_follow_up:view': { fieldName: 'owner_user_id', hasScope: true },
  'supplier_follow_up:manage': { hasScope: false },

  // Other permissions with scope
  'order:view': { fieldName: 'created_by', hasScope: true },
  'stocktake:view': { fieldName: 'created_by', hasScope: true },
  'stocktake:create_edit': { fieldName: 'created_by', hasScope: true },
  'stocktake:delete': { fieldName: 'created_by', hasScope: true },
  // Market info permissions - No scope filtering (market data is shared)
  'market_info:collection_task_view': { hasScope: false },
  'market_info:collection_task_create_edit': { hasScope: false },
  'market_info:collection_task_delete': { hasScope: false },
  'market_info:mi_product_view': { hasScope: false },
  'market_info:mi_product_edit': { hasScope: false },
  'market_info:mi_product_delete': { hasScope: false },
  'market_info:data_review_view': { hasScope: false },
  'market_info:data_review_edit_approve': { hasScope: false },
  'market_info:data_analysis_view': { hasScope: false },
  'market_info:mi_user_view': { hasScope: false },
  'market_info:mi_user_create_edit': { hasScope: false },
  'market_info:mi_user_delete': { hasScope: false },
  'warehouse_transfer:transfer_order_view': { fieldName: 'created_by', hasScope: true },
  'warehouse_transfer:transfer_order_create_edit': { fieldName: 'created_by', hasScope: true },
  'warehouse_transfer:transfer_order_submit_to_picking': {
    fieldName: 'created_by',
    hasScope: true,
  },
  'warehouse_transfer:transfer_order_delete': { fieldName: 'created_by', hasScope: true },
  'pos_system:store_account_view': { fieldName: 'store_id', hasScope: true },
  'pos_system:store_account_create_edit': { fieldName: 'store_id', hasScope: true },
  'pos_system:store_account_delete': { fieldName: 'store_id', hasScope: true },
  'pos_system:pda_account_view': { fieldName: 'store_id', hasScope: true },
  'pos_system:pda_account_create_edit': { fieldName: 'store_id', hasScope: true },
  'pos_system:pda_account_delete': { fieldName: 'store_id', hasScope: true },
  'pos_system:session_view': { fieldName: 'store_id', hasScope: true },
};

/**
 * Field name descriptions for user-friendly display
 */
const FIELD_DESCRIPTIONS: Record<string, string> = {
  buyer_id: '採購員',
  assignedTo: '指派給',
  purchaser_id: '採購員',
  owner_user_id: '負責採購員',
  created_by: '創建者',
  store_id: '門店',
  reviewer_id: '審核者',
  buyer_relationship: '採購關係',
};

/**
 * Get a human-readable description of the permission scope
 */
export function getPermissionScopeDescription(
  permissionCode: string,
  scope: 'all' | 'team' | 'self' | null,
): string {
  const config = PERMISSION_SCOPE_CONFIG[permissionCode];

  if (!config || !config.hasScope || !scope) {
    return '';
  }

  const fieldDesc = config.fieldName
    ? FIELD_DESCRIPTIONS[config.fieldName] || config.fieldName
    : '創建者';

  switch (scope) {
    case 'all':
      return '可查看所有記錄';
    case 'team':
      return `可查看${fieldDesc}是您團隊成員的記錄`;
    case 'self':
      return `只能查看${fieldDesc}是您的記錄`;
    default:
      return '';
  }
}

/**
 * Check if a permission supports scope filtering
 */
export function hasPermissionScope(permissionCode: string): boolean {
  const config = PERMISSION_SCOPE_CONFIG[permissionCode];
  return config?.hasScope || false;
}

/**
 * Get the scope badge color based on scope level
 */
export function getScopeBadgeColor(scope: 'all' | 'team' | 'self' | null): string {
  switch (scope) {
    case 'all':
      return 'green';
    case 'team':
      return 'blue';
    case 'self':
      return 'orange';
    default:
      return 'default';
  }
}

/**
 * Get the scope badge text
 */
export function getScopeBadgeText(scope: 'all' | 'team' | 'self' | null): string {
  switch (scope) {
    case 'all':
      return '所有';
    case 'team':
      return '團隊';
    case 'self':
      return '自己';
    default:
      return '無';
  }
}
